import React, { useState } from 'react';
import { PageHeader } from '../components/dashboard/PageHeader';
import { Card, CardContent } from '../components/Card';
import { Bot, Send, Sparkles, FileText, AlertTriangle, CheckCircle2, User } from 'lucide-react';
import { Button } from '../components/Button';
import { Textarea } from '../components/Textarea';
import { toast } from 'sonner';
type Msg = {
  role: 'user' | 'ai';
  text: string;
  sources?: string[];
  confidence?: number;
};
const initialThread: Msg[] = [{
  role: 'user',
  text: 'Apa isu paling berbahaya hari ini?'
}, {
  role: 'ai',
  text: 'Isu paling berbahaya saat ini adalah deepfake audio Presiden yang beredar di TikTok sejak pukul 04:12 WIB. Terdeteksi 45 video dengan audio identik, total tayangan 2.4 juta, eskalasi viral diprediksi 4-6 jam ke depan. Risiko utama: erosi kepercayaan publik terhadap program bansos dan potensi mobilisasi sosial.',
  sources: ['Deepfake Detection Module', 'Sentiment Monitoring (24J)', 'Actor Network Cluster #C-1247'],
  confidence: 92
}, {
  role: 'user',
  text: 'Mengapa sentimen pangan turun?'
}, {
  role: 'ai',
  text: 'Sentimen pangan turun 12% dalam 24 jam terakhir. Pemicu utama: (1) hoaks bansos pangan dengan 89.000 sebutan, didominasi narasi distribusi tidak merata; (2) keluhan harga pupuk di Jawa Tengah meningkat 38%; (3) operasi tagar #BansosTidakAdil menggunakan 1.247 akun bot. Rekomendasi: Kemensos rilis data resmi distribusi dalam 8 jam.',
  sources: ['Public Grievance Radar', 'Buzzer & Bot Detection', 'IPOLEKSOSBUDHANKAM — Ekonomi'],
  confidence: 88
}];
const suggestions = ['Aktor utama di balik narasi #TolakKebijakanX?', 'Risiko kunjungan Presiden ke Papua 25 Mei?', 'Apakah isu BBM subsidi organik atau terkoordinasi?', 'Brief satu halaman untuk Presiden hari ini', 'Rekomendasi kontra-narasi hoaks bansos'];
export function AiAnalyst() {
  const [thread, setThread] = useState<Msg[]>(initialThread);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const send = (text?: string) => {
    const t = (text ?? input).trim();
    if (!t) return;
    setThread(prev => [...prev, {
      role: 'user',
      text: t
    }]);
    setInput('');
    setIsTyping(true);
    setTimeout(() => {
      setThread(prev => [...prev, {
        role: 'ai',
        text: `Analisis: ${t}\n\nBerdasarkan data 24 jam terakhir, sistem mendeteksi pola signifikan terkait pertanyaan ini. Detail intelijen, aktor terkait, dan rekomendasi tindakan tersedia pada panel sumber di kanan.`,
        sources: ['Cross-Platform OSINT', 'Knowledge Graph', 'AI Risk Assessment'],
        confidence: 80 + Math.floor(Math.random() * 15)
      }]);
      setIsTyping(false);
    }, 1500);
  };
  const lastAi = [...thread].reverse().find(m => m.role === 'ai');
  return <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-6" data-id="element-1059">
      <PageHeader group="Intelijen Presiden" title="AI Presidential Analyst" description="Analis AI senior yang menjawab pertanyaan strategis dengan data, sumber, dan confidence score." icon={Bot} data-id="element-1060" />

      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0" data-id="element-1061">
        {/* Chat Area */}
        <Card className="col-span-12 lg:col-span-8 bg-card border-border/50 flex flex-col h-[700px]" data-id="element-1062">
          <CardContent className="p-0 flex flex-col h-full" data-id="element-1063">
            <div className="flex-1 overflow-y-auto p-4 space-y-6 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent" data-id="element-1064">
              {thread.map((m, i) => <div key={i} className={`flex gap-4 ${m.role === 'user' ? 'flex-row-reverse' : ''}`} data-id="element-1065">
                  {m.role === 'ai' && <div className="size-8 rounded bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0" data-id="element-1066">
                      <Bot className="size-4 text-primary" data-id="element-1067" />
                    </div>}
                  <div className={`max-w-[80%] rounded-md p-3 text-sm ${m.role === 'user' ? 'bg-primary/15 border border-primary/30 text-foreground' : 'bg-muted/30 border border-border/50 text-foreground'}`} data-id="element-1068">
                    <p className="whitespace-pre-wrap leading-relaxed" data-id="element-1069">
                      {m.text}
                    </p>
                    {m.confidence && <div className="flex items-center gap-3 mt-2 pt-2 border-t border-border/40 font-mono text-[10px] text-muted-foreground" data-id="element-1070">
                        <span className="text-emerald-500" data-id="element-1071">
                          CONFIDENCE: {m.confidence}%
                        </span>
                        <span data-id="element-1072">•</span>
                        <span data-id="element-1073">{m.sources?.length ?? 0} sumber</span>
                      </div>}
                  </div>
                  {m.role === 'user' && <div className="size-8 rounded bg-muted border border-border/50 flex items-center justify-center shrink-0" data-id="element-1074">
                      <User className="size-4" data-id="element-1075" />
                    </div>}
                </div>)}
              {isTyping && <div className="flex gap-4" data-id="element-1076">
                  <div className="size-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0" data-id="element-1077">
                    <Bot className="size-4 text-primary" data-id="element-1078" />
                  </div>
                  <div className="bg-muted/20 border border-border/50 rounded-2xl rounded-tl-none p-4 max-w-[80%] flex items-center gap-2" data-id="element-1079">
                    <span className="size-2 bg-primary/50 rounded-full animate-bounce" data-id="element-1080" />
                    <span className="size-2 bg-primary/50 rounded-full animate-bounce delay-75" data-id="element-1081" />
                    <span className="size-2 bg-primary/50 rounded-full animate-bounce delay-150" data-id="element-1082" />
                  </div>
                </div>}
            </div>

            <div className="p-4 border-t border-border/50 bg-card" data-id="element-1083">
              <div className="flex gap-2" data-id="element-1084">
                <Textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }} placeholder="Tanyakan analisis isu, aktor, atau rekomendasi tindakan..." className="min-h-[60px] resize-none bg-muted/30" data-id="element-1085" />
                <Button onClick={() => send()} className="h-[60px]" disabled={isTyping || !input.trim()} data-id="element-1086">
                  <Send className="size-4" data-id="element-1087" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Context Panel */}
        <Card className="col-span-12 lg:col-span-4 bg-card border-border/50 h-[700px] overflow-y-auto" data-id="element-1088">
          <CardContent className="p-4" data-id="element-1089">
            <h3 className="font-mono text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2 sticky top-0 bg-card/95 backdrop-blur py-2" data-id="element-1090">
              <FileText className="size-4" data-id="element-1091" /> Sumber & Rekomendasi
            </h3>

            {lastAi && <>
                <div data-id="element-1092">
                  <p className="font-mono text-[10px] text-muted-foreground mb-2" data-id="element-1093">
                    SUMBER DATA
                  </p>
                  <div className="flex flex-col gap-1.5" data-id="element-1094">
                    {lastAi.sources?.map(s => <div key={s} className="flex items-center gap-2 text-xs p-2 rounded bg-muted/30 border border-border/40" data-id="element-1095">
                        <CheckCircle2 className="size-3 text-emerald-500 shrink-0" data-id="element-1096" />
                        <span data-id="element-1097">{s}</span>
                      </div>)}
                  </div>
                </div>

                <div data-id="element-1098">
                  <p className="font-mono text-[10px] text-muted-foreground mb-2" data-id="element-1099">
                    RISIKO TERIDENTIFIKASI
                  </p>
                  <div className="flex flex-col gap-1.5" data-id="element-1100">
                    <div className="flex items-start gap-2 p-2 rounded bg-red-500/5 border border-red-500/20 text-xs" data-id="element-1101">
                      <AlertTriangle className="size-3 text-red-500 shrink-0 mt-0.5" data-id="element-1102" />
                      <span data-id="element-1103">Eskalasi viral dalam 4-6 jam</span>
                    </div>
                    <div className="flex items-start gap-2 p-2 rounded bg-amber-500/5 border border-amber-500/20 text-xs" data-id="element-1104">
                      <AlertTriangle className="size-3 text-amber-500 shrink-0 mt-0.5" data-id="element-1105" />
                      <span data-id="element-1106">Erosi kepercayaan terhadap bansos</span>
                    </div>
                  </div>
                </div>

                <div data-id="element-1107">
                  <p className="font-mono text-[10px] text-muted-foreground mb-2" data-id="element-1108">
                    REKOMENDASI TINDAKAN
                  </p>
                  <div className="flex flex-col gap-1.5" data-id="element-1109">
                    {['Komdigi: takedown deepfake (4 jam)', 'Kemensos: rilis data resmi (8 jam)', 'Tim Komunikasi: kontra-narasi (12 jam)'].map((r, i) => <div key={r} className="flex items-start gap-2 p-2 rounded bg-muted/30 border border-border/40 text-xs" data-id="element-1110">
                        <span className="font-mono text-primary" data-id="element-1111">#{i + 1}</span>
                        <span data-id="element-1112">{r}</span>
                      </div>)}
                  </div>
                  <Button size="sm" className="w-full mt-3" onClick={() => toast.success('Rekomendasi diteruskan ke Command Tasking')} data-id="element-1113">
                    Teruskan ke Tasking
                  </Button>
                </div>
              </>}
          </CardContent>
        </Card>
      </div>
    </div>;
}